import { TableBody } from '@mui/material';
import { createColumnHelper } from '@tanstack/react-table';
import moment from 'moment';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from 'styled-components';

import {
  Payment,
  StudyIssuanceRequest,
} from '../../../../shared/api/hscan/hospital';
import { usePaymentList } from '../../../../shared/hooks/hospitalHook';
import expandDown from '../../assets/icons/icon_expand-down.svg';
import expandRight from '../../assets/icons/icon_expand-right.svg';
import { IconButton } from '../../common/components/Icon';
import {
  createTableInformationContext,
  createTableElementInformationContext,
  PresentationalTableProps,
  TableAndElementInformation,
} from '../../common/components/SortableTable/PresentationalTable';
import {
  BasicTable,
  BasicTableElement,
  FragmentTableBody,
  LoadingIndicator,
} from '../../common/components/SortableTable/RenderTable';
import { GreyStatusMessage } from '../../common/components/styledComponents';
import { WindowSize, WindowSizeContext } from '../../hooks/windowSizeHook';
import { krwCurrencyFormatter as formatter } from '../../utils';

import { AdminStudiesPaymentSubTable } from './AdminStudiesPaymentSubTable';
import { CasesTable } from './CasesTable';
import { MobilePaymentTable } from './MobilePaymentTable';
import { ReceiptMessage } from './ReceiptMessage';
import { RequestedImageHistoryModal } from './RequestedImageHistoryModal';
import {
  RequestsTableForSimplePayment,
  StudyIssuanceRequestsTable,
} from './StudyIssuanceRequestsTable';
import { TrackingNumberMessage } from './TrackingNumberMessage';

export const { TableContext, TableInformationProvider } =
  createTableInformationContext<Payment, 'id'>();
export const { TableElementContext, TableElementInformationProvider } =
  createTableElementInformationContext<Payment, 'id'>(TableContext);

export const PaidStatusMessage = styled.span`
  font-size: 14px;
  color: #2e5be0;
`;

export const PayFailedStatusMessage = styled.span`
  font-size: 14px;
  color: #e97103;
`;

export const IconButtonWrapper = styled.div`
  width: 14px;
  height: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const usePaymentMethodString = (): Record<string, string> => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'features.mypage.payments.table',
  });

  return useMemo(
    () => ({
      card: `${t('paymentMethod.card')}`,
      vbank: `${t('paymentMethod.vbank')}`,
      trans: `${t('paymentMethod.trans')}`,
      phone: `${t('paymentMethod.phone')}`,
      samsung: `${t('paymentMethod.samsung')}`,
      kapy: `${t('paymentMethod.kpay')}`, // kpay인데 iamport 소스코드에 kapy라 돼있음
      cultureland: `${t('paymentMethod.cultureland')}`,
      smartculture: `${t('paymentMethod.smartculture')}`,
      happymoney: `${t('paymentMethod.happymoney')}`,
      point: `${t('paymentMethod.point')}`,
    }),
    [t],
  );
};

const DEFAULT_COUNT_PER_PAGE_OPTIONS = [10, 20, 30];

export const PaymentTable = (
  props: Omit<PresentationalTableProps<Payment, 'id'>, 'identifyKey' | 'data'>,
) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'features.mypage.payments.table',
  });

  const [page, setPage] = useState(0);
  const [countPerPage, setCountPerPage] = useState(
    DEFAULT_COUNT_PER_PAGE_OPTIONS[0],
  );
  const { data: paymentPage, isFetching } = usePaymentList(page, countPerPage);
  const rows = paymentPage?.data.content ?? [];
  const totalElements = paymentPage?.data.totalElements ?? 1;

  const paymentMethodString = usePaymentMethodString();

  const [openModal, setOpenModal] = useState(false);
  const [selectedRowId, setSelectedRowId] = useState<string>();

  const onClickStatus = useCallback((el: StudyIssuanceRequest) => {
    setSelectedRowId(el.id);
    setOpenModal(true);
  }, []);

  const columnHelper = useMemo(() => createColumnHelper<Payment>(), []);
  const cellOptions = useMemo(
    () => [
      columnHelper.display({
        id: '__expand',
        cell: ({ row }) => {
          const isExpanded = row.getIsExpanded();
          return (
            <IconButtonWrapper onClick={row.getToggleExpandedHandler()}>
              <IconButton
                imageUrl={isExpanded ? expandDown : expandRight}
                width={isExpanded ? '14px' : '8px'}
                height={isExpanded ? '8px' : '14px'}
                color="#666973"
              />
            </IconButtonWrapper>
          );
        },
        meta: {
          cellStyle: { borderBottom: { style: 'none' } },
        },
      }),
      columnHelper.display({
        id: '__REQUEST_TYPE__',
        header: `${t('header.type')}`,
        cell: ({ row }) => {
          const id = row.original.id;
          return id.startsWith('LL') ? t('type.local') : t('type.online');
        },
      }),
      columnHelper.accessor('id', {
        header: t('header.id') as string,
        meta: {
          cellStyle: { align: 'center' },
          headerStyle: { align: 'center' },
        },
      }),
      columnHelper.accessor('paidAt', {
        header: t('header.paidAt') as string,
        cell: ({ getValue }) => {
          const value = getValue();
          return value ? moment(value).format('YYYY.MM.DD HH:mm:ss') : '';
        },
        meta: {
          cellStyle: { align: 'center' },
          headerStyle: { align: 'center' },
        },
      }),
      columnHelper.accessor('paymentMethod', {
        header: t('header.paymentMethod') as string,
        cell: ({ getValue }) => {
          const value = getValue();
          return value ? paymentMethodString[value] : '';
        },
        meta: {
          cellStyle: { align: 'center' },
          headerStyle: { align: 'center' },
        },
      }),
      columnHelper.accessor('price', {
        header: t('header.price') as string,
        cell: ({ row: { original: payment } }) => {
          if (payment.type === 'SIMPLE') {
            return t('priceField', { price: formatter(payment.price.amount) });
          }
          if (payment.type === 'CD_DELIVERY') {
            return t('priceField', { price: formatter(payment.price.amount) });
          }
          if (payment.type === 'ADMIN_STUDIES_ONLINE') {
            return t('priceField', { price: formatter(payment.price.amount) });
          }
          if (payment.price.totalFee === payment.price.refundableFee) {
            // 환불된 적 없음
            return t('priceField', {
              price: formatter(payment.price.totalFee),
            });
          }
          return (
            // 일부환불 또는 전액환불
            <>
              <span
                style={{
                  textDecoration: 'line-through',
                  opacity: '0.5',
                }}>
                {t('priceField', {
                  price: formatter(payment.price.totalFee),
                })}
              </span>{' '}
              {t('priceField', {
                price: formatter(payment.price.refundableFee),
              })}
            </>
          );
        },
        meta: {
          cellStyle: { align: 'center' },
          headerStyle: { align: 'center' },
        },
      }),
      columnHelper.accessor('paymentStatus', {
        header: t('header.status') as string,
        cell: ({ getValue }) => {
          const value = getValue();
          return value === 'PAID' ? (
            <PaidStatusMessage>{`${t(
              'paymentStatus.paid',
            )}`}</PaidStatusMessage>
          ) : value === 'PARTIAL_REFUND' ? (
            <PayFailedStatusMessage>{`${t(
              'paymentStatus.partialRefund',
            )}`}</PayFailedStatusMessage>
          ) : (
            <PayFailedStatusMessage>{`${t(
              'paymentStatus.allRefund',
            )}`}</PayFailedStatusMessage>
          );
        },
        meta: {
          cellStyle: { align: 'center' },
          headerStyle: { align: 'center' },
        },
      }),
      columnHelper.accessor('receiptUrl', {
        header: t('header.receipt') as string,
        cell: ({ getValue }) => <ReceiptMessage receiptUrl={getValue()} />,
        meta: {
          cellStyle: { align: 'center' },
          headerStyle: { align: 'center' },
        },
      }),
      columnHelper.accessor('mailStatus', {
        header: t('header.trackingNumber') as string,
        cell: ({ row: { original: payment } }) =>
          payment.type === 'ISSUE' || payment.type === 'CD_DELIVERY' ? (
            <TrackingNumberMessage payment={payment} />
          ) : payment.type === 'ADMIN_STUDIES_ONLINE' ? (
            <GreyStatusMessage>{`${t('type.online')}`}</GreyStatusMessage>
          ) : null,
        meta: {
          cellStyle: { align: 'center' },
          headerStyle: { align: 'center' },
        },
      }),
    ],
    [columnHelper, paymentMethodString, t],
  );

  const windowSize = useContext(WindowSizeContext);

  const Element = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-shadow
    (props: TableAndElementInformation<Payment, 'id'>) => (
      <PaymentElement {...props} onClickStatus={onClickStatus} />
    ),
    [onClickStatus],
  );

  return (
    <TableInformationProvider
      {...props}
      disableSelect
      data={rows}
      totalCount={totalElements}
      countPerPage={countPerPage}
      countPerPageOptions={DEFAULT_COUNT_PER_PAGE_OPTIONS}
      page={page}
      onChangePage={setPage}
      onChangeCountPerPage={newCountPerPage => {
        setPage(0);
        setCountPerPage(newCountPerPage);
      }}
      cellOptions={cellOptions}
      isLoading={isFetching}
      identifyKey="id">
      {windowSize === WindowSize.MOBILE ? (
        <MobilePaymentTable onClickStatus={onClickStatus} />
      ) : (
        <BasicTable
          tableContext={TableContext}
          elementContext={TableElementContext}
          ElementProvider={TableElementInformationProvider}
          Element={Element}
          renderLoading={LoadingIndicator}
          TableBody={FragmentTableBody}
        />
      )}
      <RequestedImageHistoryModal
        id={selectedRowId ?? ''}
        open={openModal}
        onClose={() => setOpenModal(false)}
      />
    </TableInformationProvider>
  );
};

const PaymentTableBody = styled(TableBody)`
  border-bottom: 1px solid #e5e7eb;
`;

const PaymentElement = (
  props: TableAndElementInformation<Payment, 'id'> & {
    onClickStatus(request: StudyIssuanceRequest): void;
  },
) => {
  const { element: payment, onClickStatus, row, page } = props;

  useEffect(() => {
    if (row.getIsExpanded()) {
      row.toggleExpanded(false);
    }
  }, [row, page]);

  return (
    <PaymentTableBody>
      <BasicTableElement {...props} />
      {row.getIsExpanded() &&
        (payment.type === 'ISSUE' ? (
          <StudyIssuanceRequestsTable
            payment={payment}
            onClickStatus={onClickStatus}
          />
        ) : payment.type === 'CD_DELIVERY' ? (
          <CasesTable payment={payment} />
        ) : payment.type === 'ADMIN_STUDIES_ONLINE' ? (
          <AdminStudiesPaymentSubTable payment={payment} />
        ) : (
          <RequestsTableForSimplePayment />
        ))}
    </PaymentTableBody>
  );
};
